<template>
  <div v-if="items.length" class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
    <button type="button" class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800" @click="expanded = !expanded">
      <span class="font-medium text-gray-900 dark:text-gray-100">
        Excluded by quality checks ({{ items.length }})
      </span>
      <span class="text-gray-400">{{ expanded ? '−' : '+' }}</span>
    </button>
    <div v-if="expanded" class="p-4">
      <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
        {{ description }}
      </p>
      <ul class="divide-y divide-gray-100 dark:divide-gray-800">
        <li v-for="item in items" :key="item.id" class="py-2">
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-sm text-gray-900 dark:text-gray-100">{{ item.keyword }}</span>
            <span class="px-2 py-0.5 rounded-lg text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {{ tierLabel(item.tier) }}
            </span>
          </div>
          <div v-if="reasonPills(item).length" class="flex flex-wrap gap-1 mt-1">
            <span
              v-for="pill in reasonPills(item)"
              :key="pill.key"
              :class="pillClass(pill.tone)"
              class="px-2 py-0.5 rounded-lg text-xs">
              {{ pill.label }}
            </span>
          </div>
          <p v-if="reasonText(item)" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {{ reasonText(item) }}
          </p>
          <p v-if="variants(item).length" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Variants: {{ variants(item).join(', ') }}
          </p>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const props = defineProps({
  items: { type: Array, required: true },
  description: { type: String, default: '' },
  variantKeywords: { type: Function, default: () => []}
})

const expanded = ref(false)

const TIER_LABELS = {
  core: 'Core',
  high_value: 'High-Value',
  supporting: 'Supporting',
  long_tail: 'Long-Tail'
}

const tierLabel = (tier) => TIER_LABELS[tier] || tier

const pillClass = (tone) => {
  const map = {
    danger: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    warning: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    info: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
  }
  return map[tone] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
}

const reasonPills = (item) => {
  const pills = [...(item.flag_pills || [])]
  if (item.flags?.malformed && !pills.some((pill) => pill.key === 'malformed')) {
    pills.push({ key: 'malformed', label: 'Malformed', tone: 'danger' })
  }
  return pills
}

const reasonText = (item) => {
  if (item.recommendation_reasoning) return item.recommendation_reasoning
  if (item.flags?.malformed) return `Malformed keyword: ${item.flags.malformed}`
  if (item.user_decision === 'excluded') return 'Excluded during review.'
  if (item.include_recommendation === 'evaluate') return 'Needs manual evaluation; not included by default.'
  return reasonPills(item).length ? '' : 'Not included by default.'
}

const variants = (item) => props.variantKeywords(item) || []
</script>
